import React from 'react';
import { PageHeader } from '../components/PageHeader';
import { COLOR_GROUPS, ColorSwatch } from '../../../design-system/tokens/colors';

/**
 * Foundations › Colors — documentación de los tokens de color.
 *
 * Los grupos y valores salen de la fuente de verdad `COLOR_GROUPS`
 * (`src/design-system/tokens/colors.ts`), traslado del frame "Colors" del style
 * guide de Figma (node 2:82). Cada grupo: nombre + descripción a la izquierda,
 * fila de swatches (caja de color + step + hex) a la derecha.
 */

/** Un swatch: caja de color + etiqueta (step/nombre) + hex. */
const Swatch: React.FC<ColorSwatch> = ({ label, hex }) => (
  <div className="w-[120px]">
    <div
      className="h-[72px] w-full rounded-xl border border-neutral-200"
      style={{ backgroundColor: hex }}
    />
    <div className="mt-2.5 flex flex-col gap-0.5">
      <span className="text-[14px] font-semibold text-neutral-800">{label}</span>
      <span className="font-mono text-[12px] uppercase text-neutral-500">{hex}</span>
    </div>
  </div>
);

export const DSColorsPage: React.FC = () => (
  <div className="px-10 py-12">
    <div className="mx-auto flex w-full max-w-[800px] flex-col gap-14">
      <PageHeader
        title="Primitive Colors"
        description="Los colores del sistema están organizados por función y propósito: desde estilos para superficies, hasta colores de marca, semánticos e informativos. Incluyen escalas tonales consistentes y usos específicos como advertencias, errores, confirmaciones o mensajes neutrales."
      />

      <div className="flex flex-col gap-12">
        {COLOR_GROUPS.map((group) => (
          <section key={group.name} className="flex flex-col gap-5 md:flex-row md:gap-10">
            {/* Nombre + descripción del grupo */}
            <div className="w-full md:w-[260px] md:shrink-0">
              <h2 className="text-[18px] font-semibold text-neutral-900">{group.name}</h2>
              {group.description && (
                <p className="mt-1.5 text-[15px] leading-6 text-neutral-600">
                  {group.description}
                </p>
              )}
            </div>

            {/* Swatches */}
            <div className="flex flex-1 flex-wrap gap-4">
              {group.swatches.map((swatch) => (
                <Swatch key={swatch.label} {...swatch} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  </div>
);
